import {
  ALERT_SETTINGS_LIMITS,
  DEFAULT_ALERT_SETTINGS,
  alertSettingsInputSchema,
  alertSettingsSchema,
  formatDateTime,
  type AlertSettings,
} from '@fluxus/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { ErrorState, LoadingState } from '@/components/states/States';
import { Button } from '@/components/ui/button';
import { FormAlert, FormField, fieldA11y } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { errorMessage } from '@/lib/api/errors';
import { useAuth } from '@/lib/auth/session';
import { apiFieldErrors, hasFieldErrors, zodFieldErrors, type FieldErrors } from '@/lib/forms';

export const alertSettingsKey = (organizationId: string) => ['alert-settings', organizationId];

/**
 * Limites dos alertas de desvio (T-110b; DEC-044): quantos períodos seguidos de queda, quantos KPIs vermelhos
 * põem o objetivo em risco e a tolerância para lançar o realizado. Só o Admin Fluxus altera.
 */
export function AlertSettingsSection({
  organizationId,
  canEdit,
}: {
  organizationId: string;
  canEdit: boolean;
}) {
  const { client } = useAuth();
  const settings = useQuery({
    queryKey: alertSettingsKey(organizationId),
    queryFn: ({ signal }) =>
      client.request(`/organizations/${organizationId}/alert-settings`, {
        schema: alertSettingsSchema,
        signal,
      }),
  });
  return (
    <section aria-label="Limites dos alertas" className="rounded-lg border border-border bg-surface p-5">
      <h2 className="text-base font-bold">Limites dos alertas</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Quando o sistema avisa sobre um desvio. Indicador no amarelo ou no vermelho, meta atingida e ação com
        prazo vencido sempre geram alerta; estes limites ajustam os demais.
      </p>
      {settings.isPending ? (
        <LoadingState className="mt-4" rows={3} label="Carregando os limites dos alertas" />
      ) : settings.isError ? (
        <ErrorState
          className="mt-4"
          error={settings.error}
          onRetry={() => void settings.refetch()}
          retrying={settings.isFetching}
        />
      ) : (
        <AlertSettingsForm
          key={settings.data.updatedAt ?? 'padrao'}
          organizationId={organizationId}
          initial={settings.data}
          canEdit={canEdit}
        />
      )}
    </section>
  );
}

function AlertSettingsForm({
  organizationId,
  initial,
  canEdit,
}: {
  organizationId: string;
  initial: AlertSettings;
  canEdit: boolean;
}) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const [drops, setDrops] = useState(String(initial.consecutiveDropPeriods));
  const [redKpis, setRedKpis] = useState(String(initial.objectiveRedKpis));
  const [grace, setGrace] = useState(String(initial.staleGraceDays));
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const ids = { drops: useId(), red: useId(), grace: useId() };
  const L = ALERT_SETTINGS_LIMITS;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (saving) return;
    setFormError(null);
    const parsed = alertSettingsInputSchema.safeParse({
      consecutiveDropPeriods: drops.trim(),
      objectiveRedKpis: redKpis.trim(),
      staleGraceDays: grace.trim(),
    });
    if (!parsed.success) return setErrors(zodFieldErrors(parsed.error));
    setErrors({});
    setSaving(true);
    try {
      const saved = await client.request(`/organizations/${organizationId}/alert-settings`, {
        method: 'PUT',
        body: parsed.data,
        schema: alertSettingsSchema,
      });
      queryClient.setQueryData(alertSettingsKey(organizationId), saved);
      toast.success('Limites dos alertas salvos', {
        description: 'Os alertas dos projetos são reavaliados com os novos limites.',
      });
    } catch (err) {
      const fields = apiFieldErrors(err);
      if (hasFieldErrors(fields)) setErrors(fields);
      else setFormError(err);
    } finally {
      setSaving(false);
    }
  }

  const restoreDefaults = () => {
    setDrops(String(DEFAULT_ALERT_SETTINGS.consecutiveDropPeriods));
    setRedKpis(String(DEFAULT_ALERT_SETTINGS.objectiveRedKpis));
    setGrace(String(DEFAULT_ALERT_SETTINGS.staleGraceDays));
    setErrors({});
  };

  const field = (
    id: string,
    label: string,
    hint: string,
    value: string,
    set: (v: string) => void,
    error: string | undefined,
  ) => (
    <FormField
      id={id}
      label={label}
      error={error}
      hint={<p className="text-xs text-muted-foreground">{hint}</p>}
    >
      <Input
        {...fieldA11y(id, error, true)}
        value={value}
        inputMode="numeric"
        maxLength={2}
        disabled={!canEdit}
        onChange={(e) => set(e.target.value)}
        className="h-11 w-24"
      />
    </FormField>
  );

  return (
    <form noValidate onSubmit={submit} className="mt-4 flex flex-col gap-4">
      <p className="text-sm text-muted-foreground">
        {initial.updatedAt === null
          ? 'Valem os padrões sugeridos pela Fluxus.'
          : `Alterado em ${formatDateTime(initial.updatedAt)}.`}
      </p>
      <div className="grid gap-4 sm:grid-cols-3">
        {field(
          ids.drops,
          'Queda consecutiva (períodos)',
          `Avisa quando o indicador piora tantos períodos seguidos (${L.consecutiveDropPeriods.min} a ${L.consecutiveDropPeriods.max}).`,
          drops,
          setDrops,
          errors.consecutiveDropPeriods,
        )}
        {field(
          ids.red,
          'Objetivo em risco (KPIs vermelhos)',
          `Avisa quando o objetivo tem esse número de indicadores no vermelho (${L.objectiveRedKpis.min} a ${L.objectiveRedKpis.max}).`,
          redKpis,
          setRedKpis,
          errors.objectiveRedKpis,
        )}
        {field(
          ids.grace,
          'Tolerância para lançar (dias)',
          `Dias depois do fim do período até o dado contar como não atualizado (${L.staleGraceDays.min} a ${L.staleGraceDays.max}).`,
          grace,
          setGrace,
          errors.staleGraceDays,
        )}
      </div>
      {formError ? <FormAlert tone="error">{errorMessage(formError)}</FormAlert> : null}
      {canEdit ? (
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" onClick={restoreDefaults} disabled={saving}>
            Padrões dos alertas
          </Button>
          <Button type="submit" loading={saving}>
            Salvar limites dos alertas
          </Button>
        </div>
      ) : null}
    </form>
  );
}
